
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Dashboard() {
  const navigate = useNavigate();
  const [user] = useState({
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@email.com',
    phone: '(555) 123-4567',
    isReturningCustomer: true,
    referralCode: 'JOHN2025',
    totalServices: 3,
    joinDate: '2024-08-15'
  });

  const [referralLink] = useState(`${window.location.origin}/?ref=${user.referralCode}`);
  const [copySuccess, setCopySuccess] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<'returning' | 'referral' | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [codeCopySuccess, setCodeCopySuccess] = useState(false);

  useEffect(() => {
    // Set page title and meta description
    document.title = 'Customer Dashboard - Lawn Maintenance Services';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Manage your lawn maintenance account, view rewards, track service history, and share referral links for discounts.');
    }
  }, []);

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link');
    }
  };

  const shareReferralLink = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Get 15% off Lawn Maintenance Services',
        text: 'Join me and get 15% off your first lawn service!',
        url: referralLink
      });
    } else {
      copyReferralLink();
    }
  };

  const handleBookService = (discountType: 'returning' | 'referral') => {
    setSelectedDiscount(discountType);
    
    // Generate discount code based on type
    const code = discountType === 'returning' ? 'RETURN10' : 'REFER15';
    setDiscountCode(code);
    setDiscountApplied(true);
  };

  const copyDiscountCode = async () => {
    try {
      await navigator.clipboard.writeText(discountCode);
      setCodeCopySuccess(true);
      setTimeout(() => setCodeCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy code');
    }
  };

  const proceedToBooking = () => {
    setTimeout(() => {
      navigate('/');
      // Scroll to contact section after navigation
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Discount Code Modal */}
      {discountApplied && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-gift-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Your Discount Code</h3>
              <p className="text-gray-600">
                {selectedDiscount === 'returning' 
                  ? 'Your 10% returning customer discount is ready!' 
                  : 'Your 15% referral discount is ready!'}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="text-2xl font-bold text-green-600 tracking-wider">
                  {discountCode}
                </div>
                <button
                  onClick={copyDiscountCode}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-copy-line mr-2"></i>
                  Copy
                </button>
              </div>
              {codeCopySuccess && (
                <p className="text-green-600 text-sm mt-2">
                  <i className="ri-check-line mr-1"></i>
                  Code copied to clipboard!
                </p>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">How to use:</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Copy the discount code above</li>
                <li>2. Go to our contact form</li>
                <li>3. Enter the code in the "Discount Code" field</li>
                <li>4. Submit your service request</li>
              </ol>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setDiscountApplied(false);
                  setSelectedDiscount(null);
                  setDiscountCode('');
                }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
              <button
                onClick={proceedToBooking}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Welcome back, {user.firstName}!
                  </h1>
                  <p className="text-gray-600 mt-2">
                    Member since {new Date(user.joinDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long' 
                    })}
                  </p>
                </div>
                {user.isReturningCustomer && (
                  <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                    <i className="ri-vip-crown-line mr-2"></i>
                    Returning Customer
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-service-line text-blue-600 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold text-blue-600 mb-2">{user.totalServices}</div>
                  <div className="text-sm text-blue-700">Services Completed</div>
                </div>

                <div className="bg-green-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-discount-percent-line text-green-600 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold text-green-600 mb-2">10%</div>
                  <div className="text-sm text-green-700">Returning Customer Discount</div>
                </div>

                <div className="bg-purple-50 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-share-line text-purple-600 text-xl"></i>
                  </div>
                  <div className="text-2xl font-bold text-purple-600 mb-2">15%</div>
                  <div className="text-sm text-purple-700">Referral Bonus</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Rewards</h2>
                
                <div className="space-y-4">
                  <div className={`rounded-lg p-4 border transition-all duration-300 ${
                    selectedDiscount === 'returning'
                      ? 'bg-green-100 border-green-300 ring-2 ring-green-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-green-800 flex items-center">
                          Returning Customer Discount
                          {selectedDiscount === 'returning' && (
                            <i className="ri-check-circle-fill text-green-600 ml-2"></i>
                          )}
                        </h3>
                        <p className="text-sm text-green-700">
                          {selectedDiscount === 'returning' ? 'Code generated - ready to use' : 'Available on your next service'}
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-green-600">10% OFF</div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedDiscount === 'returning'
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-600 text-white'
                      }`}>
                        {selectedDiscount === 'returning' ? 'SELECTED' : 'ACTIVE'}
                      </span>
                      {selectedDiscount !== 'returning' && (
                        <button
                          onClick={() => handleBookService('returning')}
                          disabled={selectedDiscount !== null}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            selectedDiscount !== null
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          Use This
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={`rounded-lg p-4 border transition-all duration-300 ${
                    selectedDiscount === 'referral'
                      ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-200' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-blue-800 flex items-center">
                          Referral Bonus
                          {selectedDiscount === 'referral' && (
                            <i className="ri-check-circle-fill text-blue-600 ml-2"></i>
                          )}
                        </h3>
                        <p className="text-sm text-blue-700">
                          {selectedDiscount === 'referral' ? 'Code generated - ready to use' : 'Share with friends to earn'}
                        </p>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">15% OFF</div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        selectedDiscount === 'referral'
                          ? 'bg-blue-600 text-white' 
                          : 'bg-blue-600 text-white'
                      }`}>
                        {selectedDiscount === 'referral' ? 'SELECTED' : 'AVAILABLE'}
                      </span>
                      {selectedDiscount !== 'referral' && (
                        <button
                          onClick={() => handleBookService('referral')}
                          disabled={selectedDiscount !== null}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            selectedDiscount !== null
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          Use This
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {selectedDiscount && (
                  <div className="mt-6 pt-6 border-t">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start space-x-3">
                        <i className="ri-information-line text-yellow-600 text-lg mt-0.5"></i>
                        <div className="text-sm text-yellow-800">
                          <strong>Note:</strong> Only one discount can be used per service. You've selected the{' '}
                          {selectedDiscount === 'returning' ? '10% returning customer' : '15% referral'} discount.
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={() => setDiscountApplied(true)}
                      className="w-full py-3 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap bg-green-600 text-white hover:bg-green-700"
                    >
                      Get Discount Code
                    </button>
                  </div>
                )}

                {!selectedDiscount && (
                  <div className="mt-6 pt-6 border-t">
                    <p className="text-center text-gray-600 text-sm">
                      Select a reward above to generate your discount code
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Share & Earn</h2>
                
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-6">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-gift-line text-purple-600 text-2xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Refer a Friend</h3>
                    <p className="text-gray-600">
                      Share your referral link and both you and your friend get 15% off!
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Referral Code
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={user.referralCode}
                        readOnly
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      />
                      <button
                        onClick={copyReferralLink}
                        className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Referral Link
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={referralLink}
                        readOnly
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                      />
                      <button
                        onClick={copyReferralLink}
                        className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                    {copySuccess && (
                      <p className="text-green-600 text-sm mt-2">
                        <i className="ri-check-line mr-1"></i>
                        Link copied to clipboard!
                      </p>
                    )}
                  </div>

                  <button
                    onClick={shareReferralLink}
                    className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-line mr-2"></i>
                    Share Referral Link
                  </button>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-gray-900 mb-2">How it works:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Share your referral link with friends</li>
                    <li>• They get 15% off their first service</li>
                    <li>• You get 15% off your next service</li>
                    <li>• Limit: One new referral per month (different people only)</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
